import { supabase } from "@/integrations/supabase/client";

/**
 * Attaches usernames from the `public_designer_profiles` view onto saved-design
 * rows. The `profiles` table is no longer publicly readable (RLS restricts it
 * to the owner + admins), so we resolve public usernames via a dedicated
 * username-only view.
 */
export type WithUserId = { id: string; user_id?: string | null } & Record<string, unknown>;
export type WithProfiles<T> = T & { profiles: { username: string } | null };

export async function attachPublicUsernames<T extends WithUserId>(
  rows: T[],
): Promise<WithProfiles<T>[]> {
  if (!rows || rows.length === 0) return [];
  const userIds = Array.from(
    new Set(rows.map((r) => r.user_id).filter((v): v is string => !!v)),
  );

  let map: Record<string, string> = {};
  if (userIds.length > 0) {
    const { data } = await supabase
      // View is not in the generated types; cast to any.
      .from("public_designer_profiles" as never)
      .select("id, username")
      .in("id", userIds);
    const rowsTyped = (data ?? []) as Array<{ id: string; username: string }>;
    map = Object.fromEntries(rowsTyped.map((p) => [p.id, p.username]));
  }

  return rows.map((r) => ({
    ...r,
    profiles: r.user_id && map[r.user_id] ? { username: map[r.user_id] } : null,
  }));
}
